import React from "react";
import { Download, FileSpreadsheet, FileText } from "lucide-react";
import { useAdminPortal } from "../AdminPortalContext";

const AdminExportsPage = () => {
  const { activeExportFormat, downloadExport, summary, theme } =
    useAdminPortal();
  const isDarkTheme = theme === "dark";

  const exportCards = [
    {
      format: "csv",
      title: "CSV Export",
      description:
        "Spreadsheet-friendly export for filtering, sorting, and import into CRM or analytics tools.",
      icon: FileSpreadsheet,
      accent: isDarkTheme
        ? "bg-emerald-500 text-slate-950"
        : "bg-emerald-600 text-white",
    },
    {
      format: "pdf",
      title: "PDF Export",
      description:
        "Portable summary export for reporting, sharing, and archival review.",
      icon: FileText,
      accent: isDarkTheme
        ? "bg-sky-500 text-slate-950"
        : "bg-slate-900 text-white",
    },
  ];

  return (
    <div className="space-y-6">
      <section
        className={`rounded-[28px] border px-6 py-6 ${isDarkTheme ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white shadow-[0_16px_44px_rgba(15,23,42,0.05)]"}`}
      >
        <h2 className="text-2xl font-bold">Export Center</h2>
        <p
          className={`mt-3 max-w-3xl text-sm leading-7 ${isDarkTheme ? "text-slate-400" : "text-slate-500"}`}
        >
          Package lead data for operations, reporting, or external follow-up.
          More export formats and scheduled delivery can be integrated later
          without changing the UI structure.
        </p>
      </section>

      <section className="grid grid-cols-1 gap-6 xl:grid-cols-[1.2fr_0.8fr]">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {exportCards.map((card) => {
            const Icon = card.icon;
            const isActive = activeExportFormat === card.format;

            return (
              <article
                key={card.format}
                className={`rounded-[28px] border p-6 ${isDarkTheme ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white shadow-[0_16px_44px_rgba(15,23,42,0.05)]"}`}
              >
                <div
                  className={`inline-flex h-14 w-14 items-center justify-center rounded-2xl ${card.accent}`}
                >
                  <Icon className="h-6 w-6" />
                </div>
                <h3 className="mt-5 text-xl font-bold">{card.title}</h3>
                <p
                  className={`mt-3 text-sm leading-7 ${isDarkTheme ? "text-slate-400" : "text-slate-500"}`}
                >
                  {card.description}
                </p>

                <button
                  type="button"
                  onClick={() => downloadExport(card.format)}
                  disabled={isActive}
                  className={`mt-6 inline-flex items-center gap-2 rounded-2xl px-5 py-3 text-sm font-semibold transition ${card.accent} ${isActive ? "opacity-70" : "hover:opacity-90"}`}
                >
                  <Download className="h-4 w-4" />
                  {isActive
                    ? `Exporting ${card.format.toUpperCase()}...`
                    : `Download ${card.format.toUpperCase()}`}
                </button>
              </article>
            );
          })}
        </div>

        <article
          className={`rounded-[28px] border p-6 ${isDarkTheme ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white shadow-[0_16px_44px_rgba(15,23,42,0.05)]"}`}
        >
          <h3 className="text-lg font-bold">Export Snapshot</h3>
          <div className="mt-6 space-y-4">
            {[
              ["Total records", summary.totalSubmissions],
              ["New today", summary.todayCount],
              ["Last 7 days", summary.lastSevenDaysCount],
              ["Most requested service", summary.topInterest],
            ].map(([label, value]) => (
              <div
                key={label}
                className={`rounded-2xl px-4 py-4 ${isDarkTheme ? "bg-slate-950" : "bg-slate-50"}`}
              >
                <p
                  className={`text-xs font-semibold uppercase tracking-[0.22em] ${isDarkTheme ? "text-slate-500" : "text-slate-400"}`}
                >
                  {label}
                </p>
                <p className="mt-2 text-lg font-bold">{value}</p>
              </div>
            ))}
          </div>
        </article>
      </section>
    </div>
  );
};

export default AdminExportsPage;
